'use client';

import * as React from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';

type Focusable = Element & HTMLOrSVGElement;

/**
 * Duck-typed so it holds for SVG openers (a bound Icon) and for elements from
 * another document (the authoring preview iframe), where `instanceof
 * HTMLElement` fails.
 */
function isFocusable(
  element: Element | null | undefined,
): element is Focusable {
  return (
    element != null &&
    typeof (element as Partial<HTMLOrSVGElement>).focus === 'function'
  );
}

/** `document.activeElement` stops at a shadow host; follow it inside. */
function getDeepActiveElement(doc: Document): Element | null {
  let active = doc.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

/**
 * Last element the visitor pressed. iOS Safari does not focus a tapped
 * button, so the active element is `body` when the dialog opens.
 */
function useLastPressedElement(): React.RefObject<Focusable | null> {
  const pressedRef = React.useRef<Focusable | null>(null);

  React.useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const path = event.composedPath();
      const origin = (path[0] ?? event.target) as Element | null;
      const pressed = origin?.closest?.(FOCUSABLE_SELECTOR);
      pressedRef.current = isFocusable(pressed) ? pressed : null;
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  return pressedRef;
}

export interface OpenerFocusReturnHandlers {
  onOpenAutoFocus: (event: Event) => void;
  onCloseAutoFocus: (event: Event) => void;
}

/**
 * Focus return for the bottom sheet, which Vaul renders outside the shared
 * Dialog opener tracking. Radix returns focus to its own trigger ref, a
 * non-focusable host span for slotted CTAs and nothing when `modal.open`
 * opened the sheet. Remember what had focus when the sheet opened (looking
 * inside shadow roots, and accepting an SVG opener) and restore it on close,
 * falling back to the pressed element when nothing was focused. Focus the
 * visitor moved outside the sheet is left alone.
 */
export function useOpenerFocusReturn(): OpenerFocusReturnHandlers {
  const openerRef = React.useRef<Focusable | null>(null);
  const lastPressedRef = useLastPressedElement();

  return React.useMemo(
    () => ({
      onOpenAutoFocus: (event: Event) => {
        const doc = (event.currentTarget as Element | null)?.ownerDocument;
        const active = doc ? getDeepActiveElement(doc) : null;
        openerRef.current =
          isFocusable(active) && active !== doc?.body
            ? active
            : lastPressedRef.current;
      },
      onCloseAutoFocus: (event: Event) => {
        const opener = openerRef.current;
        openerRef.current = null;
        if (!opener?.isConnected || opener === opener.ownerDocument.body) {
          return;
        }
        const content = event.currentTarget as Element | null;
        const active = getDeepActiveElement(opener.ownerDocument);
        const focusMovedOutside =
          active != null &&
          active !== opener.ownerDocument.body &&
          active !== opener &&
          !content?.contains(active);
        if (focusMovedOutside) {
          return;
        }
        event.preventDefault();
        opener.focus({ preventScroll: true });
      },
    }),
    [lastPressedRef],
  );
}
